import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { EnvelopeSimple, Lock, User, GoogleLogo, ArrowLeft } from '@phosphor-icons/react';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleRegister = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div 
      className="min-h-screen bg-background flex flex-col items-center justify-center p-6 relative overflow-hidden hexagon-bg"
      data-testid="register-screen"
    >
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4AF37]/5 blur-[100px] rounded-full" />

      <div className="max-w-md w-full relative z-10">
        {/* Back button */}
        <button 
          onClick={() => navigate('/welcome')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Start Your Journey
          </h1>
          <p className="text-muted-foreground">365 days of cardio. One place to track it all.</p>
        </div>

        {/* Google Register */}
        <Button
          onClick={handleGoogleRegister}
          variant="outline"
          className="w-full border-[#D4AF37]/30 text-foreground rounded-xl py-6 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all mb-6"
          data-testid="google-register-btn"
        >
          <GoogleLogo weight="bold" className="w-5 h-5 mr-3" />
          Continue with Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-4 text-muted-foreground">Or create with email</span>
          </div>
        </div>

        {/* Register Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm text-muted-foreground">Full Name</Label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="pl-12 bg-card border-border rounded-xl py-6 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                required
                data-testid="name-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-12 bg-card border-border rounded-xl py-6 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-muted-foreground">Password</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                className="pl-12 bg-card border-border rounded-xl py-6 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                required
                minLength={6}
                data-testid="password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover mt-6"
            data-testid="submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center mt-6 text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
